import React, { useEffect, useState } from 'react';
import FadeIn from '../components/ui/FadeIn';
import Alert from '../components/ui/Alert';
import Popover from '../components/ui/Popover';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { getFullDateTime, parseTimestamp } from '../utils/date-utils';
import * as apiPoints from './apiPoints';
import { errorMessage } from './api';
import type { PlanPoint, PointRevision } from './pointsTypes';

const When: React.FC<{ iso: string | null; prefix: string }> = ({ iso, prefix }) => {
  const d = iso ? parseTimestamp(iso) : null;
  if (!d) return null;
  return (
    <span className="tabular-nums" title={getFullDateTime(iso)}>
      {prefix} {d.fromNow()}
    </span>
  );
};

interface HistoryPopoverProps {
  planId: string;
  nodeId: string;
  point: Pick<PlanPoint, 'id' | 'body' | 'edited_at'>;
  /** The "(edited)" control; the popover hangs under it. */
  anchor: React.RefObject<HTMLElement>;
  onClose: () => void;
}

/**
 * Every earlier wording of a point, newest first, under the current one. Hangs under the
 * "(edited)" control on the kit Popover.
 */
const HistoryPopover: React.FC<HistoryPopoverProps> = ({ planId, nodeId, point, anchor, onClose }) => {
  const [revisions, setRevisions] = useState<PointRevision[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setError(null);
    apiPoints
      .listRevisions(planId, nodeId, point.id)
      .then((res) => {
        if (live) setRevisions(res?.revisions ?? []);
      })
      .catch((e) => {
        if (live) setError(errorMessage(e, 'Could not load the earlier versions.'));
      });
    return () => {
      live = false;
    };
  }, [planId, nodeId, point.id, attempt]);

  return (
    // The canvas reads keys through React; this popover sits inside its tree, so typing and
    // Delete here are stopped before they reach it (the portal does not move the React parent).
    <div className="contents" onKeyDown={(e) => e.stopPropagation()}>
    <Popover open onClose={onClose} anchor={anchor} label="Earlier versions of this note" width="md">
      <div className="text-xs font-semibold text-on-surface">Now</div>
      <p className="mt-0.5 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-on-surface">{point.body}</p>
      <div className="mt-0.5 text-[11px] text-on-surface-variant">
        <When iso={point.edited_at} prefix="Edited" />
      </div>

      <div className="mt-3 border-t border-outline-variant pt-2">
        <FadeIn key={error ? 'error' : revisions === null ? 'loading' : 'ready'}>
          {error ? (
            <Alert variant="note" onRetry={() => setAttempt((n) => n + 1)}>
              {error}
            </Alert>
          ) : revisions === null ? (
            <div aria-busy="true">
              <SkeletonGroup label="Loading earlier versions" className="space-y-2 py-1">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </SkeletonGroup>
            </div>
          ) : revisions.length === 0 ? (
            <p className="text-[13px] text-on-surface-variant">No earlier versions were kept.</p>
          ) : (
            <ol className="mb-0 space-y-2.5" aria-label="Earlier versions, newest first">
              {revisions.map((r, i) => (
                <li key={`${r.created_at}-${i}`}>
                  <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-on-surface-variant">{r.body}</p>
                  <div className="mt-0.5 text-[11px] text-on-surface-variant">
                    <When iso={r.created_at} prefix="Replaced" />
                  </div>
                </li>
              ))}
            </ol>
          )}
        </FadeIn>
      </div>
    </Popover>
    </div>
  );
};

export default HistoryPopover;
